import React, { useEffect, useMemo, useState } from "react";
import { Switch } from "antd";
import { useDispatch } from "react-redux";
import { getEmployeeDirectoryAPI } from "../../../Redux/Apis/employeeApi";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import { BoardButton } from "../../atoms/BoardDesignSystem";
import "./BoardCreateListForm.scss";

/** Stable default — inline `[]` in default params is a new reference every render. */
const EMPTY_IDS = [];

const mapDirectoryToOptions = (users = []) =>
  users.map((user) => ({
    value: user.userId,
    label: user.role
      ? `${user.firstname} ${user.lastname} (${user.role})`
      : `${user.firstname} ${user.lastname}`,
  }));

const mapBoardMembersToOptions = (members = []) =>
  members
    .map((member) => {
      const id = Number(member.id || member.userId);
      if (!id) return null;
      return {
        value: id,
        label: member.name || `User ${id}`,
      };
    })
    .filter(Boolean);

const BoardCreateListForm = ({
  submitting = false,
  onSubmit,
  onCancel,
  initialName = "",
  initialVisibility = "public",
  initialCollaboratorIds = EMPTY_IDS,
  initialAddCardMemberIds = EMPTY_IDS,
  boardMembers = null,
  submitLabel = "Add list",
  autoFocusName = true,
  showAddCardPermission = true,
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState(initialName);
  const [isPublic, setIsPublic] = useState(initialVisibility !== "private");
  const [memberIds, setMemberIds] = useState(initialCollaboratorIds);
  const [restrictAddCard, setRestrictAddCard] = useState(
    (initialAddCardMemberIds || []).length > 0
  );
  const [addCardMemberIds, setAddCardMemberIds] = useState(initialAddCardMemberIds);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);

  useEffect(() => {
    setName(initialName);
    setIsPublic(initialVisibility !== "private");
    setMemberIds(initialCollaboratorIds.map(Number));
    const addIds = (initialAddCardMemberIds || []).map(Number);
    setAddCardMemberIds(addIds);
    setRestrictAddCard(addIds.length > 0);
  }, [
    initialName,
    initialVisibility,
    initialCollaboratorIds.join(","),
    initialAddCardMemberIds.join(","),
  ]);

  useEffect(() => {
    if (Array.isArray(boardMembers) && boardMembers.length) return undefined;

    let cancelled = false;
    setLoadingCollaborators(true);

    dispatch(getEmployeeDirectoryAPI())
      .unwrap()
      .then((body) => {
        if (cancelled) return;
        setDirectoryUsers(body?.data?.users ?? []);
      })
      .catch(() => {
        if (!cancelled) setDirectoryUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCollaborators(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, boardMembers]);

  const collaboratorOptions = useMemo(() => {
    if (Array.isArray(boardMembers) && boardMembers.length) {
      return mapBoardMembersToOptions(boardMembers);
    }
    return mapDirectoryToOptions(directoryUsers);
  }, [boardMembers, directoryUsers]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit?.({
      name: trimmed,
      visibility: isPublic ? "public" : "private",
      memberIds: isPublic ? [] : memberIds.map(Number).filter((id) => id > 0),
      addCardMemberIds: restrictAddCard
        ? addCardMemberIds.map(Number).filter((id) => id > 0)
        : [],
    });
  };

  return (
    <div className="board-create-list-form">
      <label className="board-create-list-form__label">List title</label>
      <input
        className="pb-list__add-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
          if (e.key === "Escape") onCancel?.();
        }}
        placeholder="Enter list title..."
        autoFocus={autoFocusName}
        disabled={submitting}
      />

      <div className="board-create-list-form__visibility">
        <div className="board-create-list-form__visibility-copy">
          <span className="board-create-list-form__label board-create-list-form__label--inline">
            {isPublic ? "Public list" : "Private list"}
          </span>
          <p className="board-create-list-form__hint">
            {isPublic
              ? "Everyone on this board can see this list."
              : "Only you and selected collaborators can see this list."}
          </p>
        </div>
        <Switch
          checked={isPublic}
          onChange={(checked) => {
            setIsPublic(checked);
            if (checked) setMemberIds([]);
          }}
          checkedChildren="Public"
          unCheckedChildren="Private"
          disabled={submitting}
        />
      </div>

      {!isPublic && (
        <div className="board-create-list-form__collaborators">
          <label className="board-create-list-form__label">Collaborators</label>
          <EmployeeSelect
            isMultiple
            value={memberIds}
            staticOptions={collaboratorOptions}
            optionsLoading={loadingCollaborators}
            excludeCeoCmo={false}
            onSelectEmployee={(ids) =>
              setMemberIds(Array.isArray(ids) ? ids : ids != null ? [ids] : [])
            }
            placeholder="Add collaborators..."
            width="100%"
            disabled={submitting}
          />
        </div>
      )}

      {showAddCardPermission && (
        <>
          <div className="board-create-list-form__visibility">
            <div className="board-create-list-form__visibility-copy">
              <span className="board-create-list-form__label board-create-list-form__label--inline">
                {restrictAddCard ? "Restricted add card" : "Anyone can add cards"}
              </span>
              <p className="board-create-list-form__hint">
                {restrictAddCard
                  ? "Only selected board members can add cards to this list."
                  : "Anyone with add-card permission can add cards here."}
              </p>
            </div>
            <Switch
              checked={restrictAddCard}
              onChange={(checked) => {
                setRestrictAddCard(checked);
                if (!checked) setAddCardMemberIds([]);
              }}
              checkedChildren="Restrict"
              unCheckedChildren="Open"
              disabled={submitting}
            />
          </div>

          {restrictAddCard && (
            <div className="board-create-list-form__collaborators">
              <label className="board-create-list-form__label">
                Who can add cards
              </label>
              <EmployeeSelect
                isMultiple
                value={addCardMemberIds}
                staticOptions={collaboratorOptions}
                optionsLoading={loadingCollaborators}
                excludeCeoCmo={false}
                onSelectEmployee={(ids) =>
                  setAddCardMemberIds(
                    Array.isArray(ids) ? ids : ids != null ? [ids] : []
                  )
                }
                placeholder="Select members who can add cards..."
                width="100%"
                disabled={submitting}
              />
            </div>
          )}
        </>
      )}

      <div className="pb-list__add-actions">
        <BoardButton
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={
            submitting
            || !name.trim()
            || (restrictAddCard && addCardMemberIds.length === 0)
          }
        >
          {submitLabel}
        </BoardButton>
        <BoardButton variant="ghost-dark" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </BoardButton>
      </div>
    </div>
  );
};

export default BoardCreateListForm;
